import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { deliveryApi, suppliersApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Package, Plus, CheckCircle, XCircle, Trash2, Truck, Bluetooth, BluetoothConnected, BluetoothOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, getTodayDate, cn } from '../lib/utils';
import { useInkbirdThermometer } from '../hooks/useInkbirdThermometer';

const emptyItem = { item_name: '', temperature: '', packaging_ok: true, quality_ok: true, notes: '' };

export default function DeliveryRecords() {
    const [records, setRecords] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({
        supplier_name: '', delivery_date: getTodayDate(),
        items: [{ ...emptyItem }], vehicle_clean: true,
        accepted: true, rejection_reason: '', notes: ''
    });
    const thermometer = useInkbirdThermometer();

    const loadRecords = () => {
        setLoading(true);
        deliveryApi.getAll().then(({ data }) => setRecords(data)).catch(() => toast.error('Failed to load records')).finally(() => setLoading(false));
    };

    useEffect(() => {
        loadRecords();
        suppliersApi.getAll().then(({ data }) => setSuppliers(data)).catch(() => {});
    }, []);

    const addItem = () => setForm({ ...form, items: [...form.items, { ...emptyItem }] });
    const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
    const updateItem = (i, field, value) => {
        const items = [...form.items];
        items[i] = { ...items[i], [field]: value };
        setForm({ ...form, items });
    };

    const handleCreate = async () => {
        if (!form.supplier_name) { toast.error('Supplier name is required'); return; }
        if (form.items.some(i => !i.item_name)) { toast.error('All items need a name'); return; }
        try {
            await deliveryApi.create({
                supplier_name: form.supplier_name,
                delivery_date: form.delivery_date,
                items: form.items.map(i => ({
                    item_name: i.item_name,
                    temperature: i.temperature !== '' ? parseFloat(i.temperature) : null,
                    packaging_ok: i.packaging_ok,
                    quality_ok: i.quality_ok,
                    notes: i.notes || null,
                })),
                vehicle_clean: form.vehicle_clean,
                accepted: form.accepted,
                rejection_reason: !form.accepted ? form.rejection_reason : null,
                notes: form.notes || null,
            });
            toast.success('Delivery record saved');
            setShowForm(false);
            setForm({ supplier_name: '', delivery_date: getTodayDate(), items: [{ ...emptyItem }], vehicle_clean: true, accepted: true, rejection_reason: '', notes: '' });
            loadRecords();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save');
        }
    };

    return (
        <Layout>
            <div data-testid="delivery-records-page">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
                            <Package className="h-6 w-6 brand-icon" /> Delivery Acceptance Records
                        </h1>
                        <p className="text-slate-400 text-sm mt-1">Log and track delivery checks as per SFBB requirements</p>
                    </div>
                    <Button onClick={() => setShowForm(!showForm)} className="brand-btn font-medium" data-testid="add-delivery-button">
                        <Plus className="h-4 w-4 mr-1" /> New Record
                    </Button>
                </div>

                {showForm && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 mb-6" data-testid="delivery-form">
                        <h2 className="text-lg font-semibold text-slate-200 mb-4">Record Delivery Check</h2>

                        {/* Thermometer status bar */}
                        {thermometer.bleSupported && (
                            <div className={cn(
                                "flex items-center gap-3 p-2.5 rounded-lg border text-sm mb-4",
                                thermometer.connected ? "bg-emerald-500/10 border-emerald-500/30" : "bg-slate-800/50 border-slate-700"
                            )} data-testid="delivery-thermometer-status">
                                {thermometer.connected ? (
                                    <>
                                        <BluetoothConnected className="h-4 w-4 text-emerald-400 shrink-0" />
                                        <span className="text-emerald-400 font-medium flex-1 truncate">
                                            {thermometer.deviceName} — {thermometer.temperature !== null ? `${thermometer.temperature.toFixed(1)}°C` : 'Waiting...'}
                                        </span>
                                        <Button size="sm" variant="outline" onClick={thermometer.disconnect}
                                            className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs h-7 shrink-0" data-testid="delivery-disconnect-thermometer">
                                            <BluetoothOff className="h-3 w-3 mr-1" /> Disconnect
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Bluetooth className="h-4 w-4 text-slate-400 shrink-0" />
                                        <span className="text-slate-400 flex-1">Connect thermometer to use "Use Reading" on items</span>
                                        <Button size="sm" onClick={async () => {
                                            const r = await thermometer.connect();
                                            if (r.success) toast.success(`Connected to ${r.deviceName}`);
                                            else if (r.error !== 'Pairing cancelled') toast.error(r.error);
                                        }} disabled={thermometer.connecting}
                                            className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-7 shrink-0" data-testid="delivery-connect-thermometer">
                                            {thermometer.connecting ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Connecting...</> : <><Bluetooth className="h-3 w-3 mr-1" /> Connect</>}
                                        </Button>
                                    </>
                                )}
                            </div>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-slate-300">Supplier *</Label>
                                <Input list="supplier-list" value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="Type or select supplier" data-testid="delivery-supplier" />
                                <datalist id="supplier-list">
                                    {suppliers.map(s => <option key={s.id} value={s.name} />)}
                                </datalist>
                            </div>
                            <div>
                                <Label className="text-slate-300">Delivery Date</Label>
                                <Input type="date" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" data-testid="delivery-date" />
                            </div>
                            <div>
                                <Label className="text-slate-300">Vehicle Clean?</Label>
                                <select value={form.vehicle_clean ? 'yes' : 'no'} onChange={(e) => setForm({ ...form, vehicle_clean: e.target.value === 'yes' })}
                                    className="mt-1 w-full h-10 rounded-md border border-slate-700 bg-slate-800 px-3 text-sm text-slate-100" data-testid="delivery-vehicle-clean">
                                    <option value="yes">Yes</option>
                                    <option value="no">No</option>
                                </select>
                            </div>
                            <div>
                                <Label className="text-slate-300">Delivery Accepted?</Label>
                                <select value={form.accepted ? 'yes' : 'no'} onChange={(e) => setForm({ ...form, accepted: e.target.value === 'yes' })}
                                    className="mt-1 w-full h-10 rounded-md border border-slate-700 bg-slate-800 px-3 text-sm text-slate-100" data-testid="delivery-accepted">
                                    <option value="yes">Yes - Accepted</option>
                                    <option value="no">No - Rejected</option>
                                </select>
                            </div>
                        </div>

                        {!form.accepted && (
                            <div className="mt-4">
                                <Label className="text-slate-300">Rejection Reason *</Label>
                                <Input value={form.rejection_reason} onChange={(e) => setForm({ ...form, rejection_reason: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="Why was the delivery rejected?" />
                            </div>
                        )}

                        {/* Items */}
                        <div className="mt-5">
                            <div className="flex items-center justify-between mb-3">
                                <Label className="text-slate-200 font-semibold">Items Received</Label>
                                <Button type="button" size="sm" variant="outline" onClick={addItem}
                                    className="border-slate-700 text-slate-300 hover:bg-slate-800 text-xs h-7" data-testid="add-delivery-item">
                                    <Plus className="h-3 w-3 mr-1" /> Add Item
                                </Button>
                            </div>
                            <div className="space-y-3">
                                {form.items.map((item, i) => (
                                    <div key={i} className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50" data-testid={`delivery-item-${i}`}>
                                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
                                            <div className="col-span-2 sm:col-span-1">
                                                <Label className="text-slate-400 text-xs">Item *</Label>
                                                <Input value={item.item_name} onChange={(e) => updateItem(i, 'item_name', e.target.value)}
                                                    className="mt-0.5 bg-slate-800 border-slate-700 text-slate-100 h-8 text-sm" placeholder="Item name" />
                                            </div>
                                            <div>
                                                <Label className="text-slate-400 text-xs">Temp (°C)</Label>
                                                <div className="flex gap-1 mt-0.5">
                                                    <Input type="number" step="0.1" value={item.temperature} onChange={(e) => updateItem(i, 'temperature', e.target.value)}
                                                        className="bg-slate-800 border-slate-700 text-slate-100 h-8 text-sm flex-1" placeholder="°C" />
                                                    {thermometer.connected && thermometer.temperature !== null && (
                                                        <Button type="button" size="sm" variant="outline"
                                                            className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 h-8 px-2 text-xs shrink-0"
                                                            onClick={() => updateItem(i, 'temperature', thermometer.temperature.toFixed(1))}
                                                            data-testid={`delivery-use-reading-${i}`}>
                                                            Use Reading
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                            <div>
                                                <Label className="text-slate-400 text-xs">Packaging</Label>
                                                <select value={item.packaging_ok ? 'ok' : 'bad'} onChange={(e) => updateItem(i, 'packaging_ok', e.target.value === 'ok')}
                                                    className="mt-0.5 w-full h-8 rounded-md border border-slate-700 bg-slate-800 px-2 text-xs text-slate-100">
                                                    <option value="ok">OK</option>
                                                    <option value="bad">Damaged</option>
                                                </select>
                                            </div>
                                            <div>
                                                <Label className="text-slate-400 text-xs">Quality</Label>
                                                <select value={item.quality_ok ? 'ok' : 'bad'} onChange={(e) => updateItem(i, 'quality_ok', e.target.value === 'ok')}
                                                    className="mt-0.5 w-full h-8 rounded-md border border-slate-700 bg-slate-800 px-2 text-xs text-slate-100">
                                                    <option value="ok">OK</option>
                                                    <option value="bad">Issue</option>
                                                </select>
                                            </div>
                                            <div className="flex items-end">
                                                {form.items.length > 1 && (
                                                    <Button type="button" size="sm" variant="ghost" className="text-rose-400 hover:bg-rose-500/10 h-8 w-8 p-0"
                                                        onClick={() => removeItem(i)}><Trash2 className="h-3.5 w-3.5" /></Button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="mt-4">
                            <Label className="text-slate-300">Notes</Label>
                            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="Additional notes" />
                        </div>

                        <div className="flex gap-2 mt-4">
                            <Button onClick={handleCreate} className="brand-btn font-medium" data-testid="save-delivery-button">Save Record</Button>
                            <Button variant="outline" onClick={() => setShowForm(false)} className="border-slate-700 text-slate-300">Cancel</Button>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="h-20 bg-slate-900/60 rounded-xl animate-pulse" />)}</div>
                ) : records.length === 0 ? (
                    <div className="text-center py-16 text-slate-500">
                        <Package className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p>No delivery records yet</p>
                        <p className="text-xs mt-1">SFBB requires checking all deliveries on arrival</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {records.map((rec) => {
                            const allOk = rec.accepted && rec.vehicle_clean;
                            return (
                                <div key={rec.id} className="bg-slate-900/60 border border-slate-800 rounded-xl p-4" data-testid={`delivery-record-${rec.id}`}>
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                        <div className={`p-2 rounded-lg shrink-0 ${allOk ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'}`}>
                                            {allOk ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <h3 className="text-sm font-semibold text-slate-200">{rec.supplier_name}</h3>
                                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${rec.accepted ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'}`}>
                                                    {rec.accepted ? 'ACCEPTED' : 'REJECTED'}
                                                </span>
                                                {!rec.vehicle_clean && <span className="px-2 py-0.5 rounded-full text-xs bg-amber-500/20 text-amber-400">Vehicle Issue</span>}
                                            </div>
                                            <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
                                                <span>{formatDate(rec.delivery_date)}</span>
                                                <span>{rec.items?.length || 0} item(s)</span>
                                                <span>By: {rec.recorded_by_name}</span>
                                            </div>
                                            {rec.rejection_reason && <p className="text-xs text-rose-400 mt-1">Reason: {rec.rejection_reason}</p>}
                                            {rec.items && rec.items.length > 0 && (
                                                <div className="mt-2 flex flex-wrap gap-1.5">
                                                    {rec.items.map((item, i) => (
                                                        <span key={i} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${item.packaging_ok && item.quality_ok ? 'bg-slate-800 text-slate-300' : 'bg-rose-500/10 text-rose-400'}`}>
                                                            {item.item_name}
                                                            {item.temperature != null && <span className="text-slate-500">({item.temperature}°C)</span>}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0">
                                            <Truck className="h-3.5 w-3.5 text-slate-600" />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </Layout>
    );
}
